import { type FC, useCallback } from "react";

import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectIsCartOnlyStandaloneGifts } from "@jti/store/selectors/selectIsCartOnlyStandaloneGifts";
import { cartMiniSlice } from "@jti/store/slices/cartMiniSlice";
import { useTranslations } from "@jti/ui";

import styles from "./CartMiniFooter.module.scss";

type CartMiniFooterProps = {
  hasShadow?: boolean;
  disableCheckoutButton?: boolean;
  onCheckoutClick: () => void;
  isCartEmpty: boolean;
  isVisible?: boolean;
  shopButtonLabel?: string;
};

export const CartMiniFooter: FC<CartMiniFooterProps> = ({
  hasShadow = false,
  onCheckoutClick,
  disableCheckoutButton = false,
  isCartEmpty,
  isVisible = false,
  shopButtonLabel,
}) => {
  const { __ } = useTranslations();
  const { SHOP_URL } = useConfig();
  const dispatch = useDispatch();

  const isCartOnlyStandaloneGifts = useSelector(selectIsCartOnlyStandaloneGifts);

  const handleShopClick = useCallback(() => {
    dispatch(cartMiniSlice.actions.close());
  }, [dispatch]);

  if (!isVisible) return null;

  return (
    <div className={classNames(styles.footerContainer, { [styles.footerShadow]: hasShadow })}>
      {isCartEmpty ? (
        <Button
          fullWidth
          size="lg"
          href={SHOP_URL}
          variant="secondary"
          className={styles.button}
          onClick={handleShopClick}
          data-testid="miniCartCheckoutButton"
        >
          {shopButtonLabel}
        </Button>
      ) : (
        <Button
          fullWidth
          size="lg"
          variant="primary"
          className={styles.button}
          disabled={disableCheckoutButton || isCartOnlyStandaloneGifts}
          onClick={onCheckoutClick}
          data-testid="miniCartCheckoutButton"
        >
          {__("cart.cart_mini.button.checkout")}
        </Button>
      )}
    </div>
  );
};
